import { TAB_DIRECTORIES_MAPPING } from './SORTableColumns'

export interface DataConfig {
  required: string[]
  optional: string[]
}

export const SOR_DATA_CONFIGS: Record<
  keyof typeof TAB_DIRECTORIES_MAPPING,
  DataConfig
> = {
  'production-requirements': {
    required: ['productCode', 'quantity'],
    optional: [
      'productName',
      'quantityMade',
      'quantityStillToMake',
      'dontStartBefore',
      'finishBefore',
      'finishBeforeHard',
    ],
  },
  'workstation-downtime': {
    required: ['resource', 'downTime'],
    optional: ['resourceType', 'site_location', 'resourceGroupCode'],
  },
  'product-master-data': {
    required: ['productCode', 'inputProductCode', 'conversionFactor'],
    optional: [
      'productName',
      'inputProductName',
      'usageGroup',
      'requiredTemperature',
      'requiresSterilisation',
      'basketFamilyId',
      'format',
      'productGroup',
    ],
  },
  'packing-routes': {
    required: [
      'baseProductCode',
      'kitchen',
      'kitchenRunRate',
      'lineRunRate1',
      'packingLine1',
      'pr1',
      'productCode',
      'maxRunTime',
    ],
    optional: [
      'baseProductName',
      'lineRunRate2',
      'lineRunRate3',
      'lineRunRate4',
      'packingLine2',
      'packingLine3',
      'packingLine4',
      'pr2',
      'pr3',
      'pr4',
      'productName',
      'prepTime',
      'inputPrepTime',
      'minRunTime',
      'preferredRunTime',
      'maxBatches',
      // Holding tanks (V1). All nullable and absent-tolerant: a route that
      // sets none of them is an ordinary single-segment route. They are
      // optional rather than required for exactly that reason — but they must
      // be declared, because this contract also filters the CSV template the
      // tab downloads, so an omitted column cannot be filled in by a planner.
      // The tank goes in the Kitchen / Line column; inputTankId, outputTankId
      // and inputProductCode are deliberately no longer offered (v7.152.0).
      'routeId',
      'segmentIndex',
      'maturationHours',
    ],
  },
  'cleaning-mapping': {
    required: ['asset'],
    optional: [
      'programName',
      'cip',
      'cipChannel',
      'prepTime',
      'preRinseTime',
      'causticTime',
      'finalRinseTime',
      'drainTime',
      'totalTargetTime',
    ],
  },
  'resource-and-material-constraints': {
    required: ['ResourceConstraint', 'ConstraintAmount'],
    optional: ['ConstraintType', 'UOM', 'openingFreeBaskets', 'fullBaskets'],
  },
  'resource-and-input-mapping': {
    required: ['inputProductCode', 'resource'],
    optional: ['inputProductName', 'runRate', 'workStationCode'],
  },
  'resource-and-output-mapping': {
    required: ['productCode', 'resource'],
    optional: ['productName', 'runRate', 'workStationCode'],
  },
  'product-clean-changeover': {
    required: ['line_code', 'previous_product_code', 'next_product_code'],
    optional: [
      'remove_clean',
      'remove_offset',
      'other_changeover',
      'changeover_cost',
      'changeover_environmental_cost',
    ],
  },
  'base-material-clean-changeover': {
    required: [
      'kitchen_code',
      'previous_material_code',
      'next_base_material_code',
    ],
    optional: [
      'remove_clean',
      'other_changeover',
      'changeover_cost',
      'changeover_environmental_cost',
    ],
  },
  'steriliser-types': {
    required: [
      'steriliserTypeId',
      'basketsPerCycle',
      'cycleHours',
      'instanceCount',
    ],
    optional: [],
  },
  'steriliser-routing': {
    required: ['steriliserTypeId', 'basketFamilyId', 'lines'],
    optional: [],
  },
  // Both mirror the per-schedule grids' own dataConfig exactly — a column
  // required there and optional here would let a CSV pass the master upload
  // and then fail on the schedule it seeds.
  'resource-switching': {
    required: ['previous_resource', 'next_resource', 'switching_time'],
    optional: ['resource_constraint', 'resource_amount'],
  },
  'format-changeover': {
    required: ['format_from', 'format_to', 'changeover_time'],
    optional: ['changeover_cost', 'resource_constraint', 'resource_amount'],
  },
  // Every hour optional: a CSV naming only the hours that differ from the
  // flat amount is the intended way to author a shift pattern.
  'resource-capacity-profile': {
    required: ['ResourceConstraint'],
    optional: Array.from({ length: 168 }, (_unused, h) => `h${h}`),
  },
  // Only the vessel's name is required. `allowAccumulation` is what makes
  // `capacity` bind — without it a filling is one kitchen run and the vessel
  // imposes no ceiling — so the two travel together; `UOM` is display only.
  tanks: {
    required: ['tankId'],
    optional: ['capacity', 'UOM', 'maxHoldHours', 'allowAccumulation'],
  },
  // Both halves key the row, so both are required — a blank productCode would
  // merge every eligibility row for a tank into one.
  'tank-eligibility': {
    required: ['tankId', 'productCode'],
    optional: [],
  },
  // Every column is needed: the engine refuses a row missing any of them.
  'kitchen-cycles': {
    required: ['kitchen', 'baseMaterialCode', 'position'],
    optional: [],
  },
}
